/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { NumberField } from '../../../../../common/NumberField';
import { CustomTextField } from '../../../../../common/CustomTextField';
import { useAttachmentsValidations } from '../../../hooks/useAttachmentsValidations';
import { specialDocumentsInAnotherLanguage, specialDocumentTypes } from '../../../services/constants';

const useStyles = makeStyles(theme => ({
  fromPage: {
    marginRight: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    width: '9rem',
  },
  toPage: {
    marginRight: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    width: '9rem',
  },
  technicalField: {
    marginBottom: theme.spacing(1),
    width: '19rem',
  },
}));

export function ToFromPages({ type, readOnly, form, hiddenInputs }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { errors, register, clearErrors } = form;
  const {
    requiredNumberValidation,
    pageNumberValidation,
    figureToBePublishedValidation,
    attachmentsConfigReady,
  } = useAttachmentsValidations();

  return (
    attachmentsConfigReady && (
      <>
        <NumberField
          required={true}
          className={classes.fromPage}
          testId={`${type}from`}
          name={`${type}from`}
          id={`${type}from`}
          label={t('pagination.from.page')}
          readOnly={readOnly}
          errors={errors}
          extraErrors={!!errors.gap || !!errors.overlap}
          register={register}
          validations={pageNumberValidation({ form, type, fieldName: 'pagination.from.page' })}
          onChange={() => {
            // Clear any existing error for the field after making an update
            // Field validation shall be triggered after clicking Upload File only
            if (errors[`${type}from`]) clearErrors([`${type}from`]);
            if (errors.overlap) clearErrors(['overlap']);
          }}
        />
        <NumberField
          required={true}
          className={classes.toPage}
          testId={`${type}to`}
          name={`${type}to`}
          id={`${type}to`}
          label={t('pagination.to.page')}
          readOnly={readOnly}
          errors={errors}
          extraErrors={!!errors.gap || !!errors.overlap}
          register={register}
          validations={pageNumberValidation({ form, type, fieldName: 'pagination.to.page' })}
          onChange={() => {
            // Clear any existing error for the field after making an update
            // Field validation shall be triggered after clicking Upload File only
            if (errors[`${type}to`]) clearErrors([`${type}to`]);
            if (errors.overlap) clearErrors(['overlap']);
          }}
        />
        {((!hiddenInputs?.includes('numberOfClaims') && type.includes(specialDocumentTypes.CLAIMS)) ||
          type.includes(specialDocumentsInAnotherLanguage.CLAIMS_IN_ANOTHER_LANGUAGE)) && (
          <NumberField
            required={true}
            className={classes.technicalField}
            testId={`${type}numberOfClaims`}
            name={`${type}numberOfClaims`}
            id={`${type}numberOfClaims`}
            label={t('claims.number')}
            readOnly={readOnly}
            errors={errors}
            register={register}
            validations={requiredNumberValidation('claims.number')}
            onChange={() => {
              // Clear any existing error for the field after making an update
              // Field validation shall be triggered after clicking Upload File only
              if (errors[`${type}numberOfClaims`]) clearErrors(`${type}numberOfClaims`);
            }}
          />
        )}
        {((!hiddenInputs?.includes('numberOfDrawings') && type.includes(specialDocumentTypes.DRAWINGS)) ||
          type.includes(specialDocumentsInAnotherLanguage.DRAWINGS_IN_ANOTHER_LANGUAGE)) && (
          <NumberField
            className={classes.technicalField}
            testId={`${type}numberOfDrawings`}
            name={`${type}numberOfDrawings`}
            id={`${type}numberOfDrawings`}
            label={t('drawings.number')}
            readOnly={readOnly}
            errors={errors}
            register={register}
            validations={requiredNumberValidation('drawings.number')}
            onChange={() => {
              // Clear any existing error for the field after making an update
              // Field validation shall be triggered after clicking Upload File only
              if (errors[`${type}numberOfDrawings`]) clearErrors(`${type}numberOfDrawings`);
            }}
          />
        )}
        {((!hiddenInputs?.includes('figureToBePublished') && type.includes(specialDocumentTypes.ABSTRACT)) ||
          type.includes(specialDocumentsInAnotherLanguage.ABSTRACT_IN_ANOTHER_LANGUAGE)) && (
          <CustomTextField
            className={classes.technicalField}
            testId={`${type}figureToBePublished`}
            name={`${type}figureToBePublished`}
            id={`${type}figureToBePublished`}
            label={t('attachments.abstract.figure.published')}
            readOnly={readOnly}
            errors={errors}
            InputProps={{ inputProps: { maxLength: 4 } }}
            inputRef={register(figureToBePublishedValidation())}
            register={register}
            onChange={() => {
              // Clear any existing error for the field after making an update
              // Field validation shall be triggered after clicking Upload File only
              if (errors[`${type}figureToBePublished`]) clearErrors(`${type}figureToBePublished`);
            }}
          />
        )}
      </>
    )
  );
}
